import { useEffect, type ReactNode } from 'react';
import { ErrorBoundary } from '@/app/ErrorBoundary';
import { AsciiFace } from '@/chrome/AsciiFace/AsciiFace';
import { CowardWatermark } from '@/chrome/CowardWatermark/CowardWatermark';
import { useCursorMode } from '@/chrome/Cursor/useCursorMode';
import { DialogueBox } from '@/chrome/DialogueBox/DialogueBox';
import { Monitor } from '@/chrome/Monitor/Monitor';
import { ScanlineOverlay } from '@/chrome/ScanlineOverlay/ScanlineOverlay';
import { TokenTray } from '@/chrome/TokenTray/TokenTray';
import { useCheaterDetection } from '@/hooks/useCheaterDetection';
import { useAfkPanic } from '@/hooks/useAfkPanic';
import { useSlowPlay } from '@/hooks/useSlowPlay';
import { useGameStore } from '@/state/gameStore';
import {
  selectActiveV2Event,
  selectBezelState,
  selectFaceState,
  selectMonitorBrand,
  selectNetworkAccessChoice,
  selectRecoveryPhase,
  selectServiceDeskRevealed,
} from '@/state/selectors';
import type { LevelId } from '@/state/types';
import { DEFAULT_MONITOR_BRAND } from '@/state/types';
import { LevelCrash } from './LevelCrash';
import { getLevelDisplayLabel } from './levelDisplay';
import styles from './LevelShell.module.css';

export interface LevelShellProps {
  id: LevelId;
  children: ReactNode;
}

export function LevelShell({ id, children }: LevelShellProps) {
  const face = useGameStore(selectFaceState);
  const bezelState = useGameStore(selectBezelState);
  const monitorBrand = useGameStore(selectMonitorBrand);
  const activeV2Event = useGameStore(selectActiveV2Event);
  const serviceDeskAvailable = useGameStore(selectServiceDeskRevealed);
  const recoveryPhase = useGameStore(selectRecoveryPhase);
  const networkAccessChoice = useGameStore(selectNetworkAccessChoice);
  const { reset: cursorReset } = useCursorMode();
  const levelBadge = activeV2Event === null ? getLevelDisplayLabel(id) : 'SYSTEM INTERRUPT';
  const isDraggableLevelBadge = id.kind === 'LEVEL' && id.n === 27;
  const areTokensDraggable = id.kind === 'LEVEL' && id.n === 16;
  const coldRestartActive = activeV2Event === 'COLD_RESTART';
  const systemHalted = coldRestartActive && recoveryPhase === 'HALTED';
  const networkAccessActive = activeV2Event === 'NETWORK_ACCESS';

  useSlowPlay(id, activeV2Event === null);
  useAfkPanic(activeV2Event === null);
  useCheaterDetection();
  // Solve the L29 cursor-leak concern here, once, not per-level.
  useEffect(() => {
    return () => {
      cursorReset();
    };
  }, [cursorReset]);

  return (
    <>
      <Monitor
        bezelState={bezelState}
        brand={monitorBrand ?? DEFAULT_MONITOR_BRAND}
        serviceAvailable={serviceDeskAvailable}
        systemHalted={systemHalted}
        restartAvailable={systemHalted}
        onRestart={() => {
          useGameStore.getState().startRecoveryBoot();
        }}
        networkPortVisible={networkAccessActive}
        networkCableConnected={networkAccessChoice === 'CONNECTED'}
        networkControlDisabled={networkAccessChoice !== null}
        onNetworkConnect={() => {
          useGameStore.getState().chooseNetworkAccess('CONNECTED');
        }}
        tokenBank={<TokenTray draggableTokens={areTokensDraggable} />}
      >
        <div
          className={styles.layout}
          data-testid="level-layout"
          data-v2-event={activeV2Event ?? 'none'}
        >
          <header className={styles.header}>
            <div className={styles.faceSlot}>
              <AsciiFace state={face} />
            </div>
            <div
              className={styles.levelBadge}
              data-testid="level-badge"
              draggable={isDraggableLevelBadge}
              onDragStart={(e) => {
                if (!isDraggableLevelBadge) return;
                e.dataTransfer.setData('text', levelBadge);
              }}
            >
              {levelBadge}
            </div>
          </header>

          <div className={styles.stage} data-testid="level-stage">
            <ErrorBoundary fallback={<LevelCrash id={id} />}>{children}</ErrorBoundary>
          </div>

          <footer className={styles.footer}>
            <DialogueBox />
          </footer>

          <CowardWatermark />
        </div>
      </Monitor>
      <ScanlineOverlay />
    </>
  );
}
